import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { MinimizedChat } from '../../types/chat';
import { useToast } from '../../contexts/ToastContext';
import { useChatContext } from '../../contexts/AppDataContext';
import { useUnifiedData } from '../../contexts/AppDataContext';
import { useWindow } from '../../contexts/WindowContext';
import { useSidebar } from '../../contexts/SideBarContext';
import SidebarItemDropdown from './SidebarItemDropdown';

interface CategorizedChats {
  today: MinimizedChat[];
  yesterday: MinimizedChat[];
  lastWeek: MinimizedChat[];
  lastMonth: MinimizedChat[];
  older: MinimizedChat[];
}

const SidebarChatsSkeleton: React.FC = () => (
  <>
    <h4 className="sidebar-section-title">
      Today
    </h4>
    <div className="chat-list-skeleton-container">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
        <div key={i} className="chat-list-item-skeleton">
          <div className="chat-list-item-skeleton-title" />
        </div>
      ))}
    </div>
  </>
);

interface ChatListProps {
  isSidebarOpen: boolean;
  onChatRemove?: (chatId: string) => void;
  onPaginationStateChange?: (hasMoreChats: boolean, isLoadingMore: boolean, loadMoreChats: () => void) => void;
}

const ChatList: React.FC<ChatListProps> = ({ isSidebarOpen, onChatRemove, onPaginationStateChange }) => {
  const toast = useToast();
  const { currentChatId } = useChatContext();
  const {
    chats,
    isChatsLoading,
    togglePinChat,
    removeChat,
    renameChat
  } = useUnifiedData();
  const {
    windowWidth,
    openConfirmationModal
  } = useWindow()
  const {
    setIsSidebarOpen
  } = useSidebar()

  const [sortedChats, setSortedChats] = useState<MinimizedChat[]>([]);
  const [paginatedChats, setPaginatedChats] = useState<MinimizedChat[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [removingChatId, setRemovingChatId] = useState<string | null>(null);
  const CHATS_PER_PAGE = 20;

  useEffect(() => {
    if (chats) {
      const filtered = chats.filter((chat: any) => !chat.spaceId);
      const sorted = [...filtered].sort((a, b) => 
        new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
      );
      setSortedChats(sorted);
      setCurrentPage(1);
      setPaginatedChats(sorted.slice(0, CHATS_PER_PAGE));
      setHasMoreChats(sorted.length > CHATS_PER_PAGE);
    } else {
      setSortedChats([]);
      setPaginatedChats([]);
      setCurrentPage(1);
      setHasMoreChats(false);
    }
  }, [chats, CHATS_PER_PAGE]);

  const categorizedChats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);

    const nonPinnedChats = paginatedChats.filter(chat => !chat.isPinned);

    return nonPinnedChats.reduce<CategorizedChats>((acc, chat) => {
      const chatDate = new Date(chat.lastModified);
      if (chatDate >= today) {
        acc.today.push(chat);
      } else if (chatDate >= yesterday) {
        acc.yesterday.push(chat);
      } else if (chatDate >= lastWeek) {
        acc.lastWeek.push(chat);
      } else if (chatDate >= lastMonth) {
        acc.lastMonth.push(chat);
      } else {
        acc.older.push(chat);
      }
      return acc;
    }, { today: [], yesterday: [], lastWeek: [], lastMonth: [], older: [] });
  }, [paginatedChats]);

  const handleRemoveChat = useCallback(async (id: string) => {
    const chat = chats.find(c => c.id === id);
    const chatTitle = chat?.title || 'this chat';

    const confirmDelete = () => {
      setRemovingChatId(id);
      setTimeout(async () => {
        try {
          await removeChat(id);
          localStorage.removeItem(`chat_${id}`);
          if (id === currentChatId) {
            onChatRemove?.(id);
          }
        } catch (error) {
          console.error('Error removing chat:', error);
        } finally {
          setRemovingChatId(null);
        }
      }, 400);
    };

    // Use confirmation modal from WindowContext
    openConfirmationModal(
      `Delete "${chatTitle}"?`,
      confirmDelete,
      'This action cannot be undone.'
    );
  }, [chats, currentChatId, onChatRemove, removeChat, openConfirmationModal]);

  const handleTogglePin = useCallback(async (id: string) => {
    await togglePinChat(id);
  }, [togglePinChat]);

  const handleStartRename = useCallback((chat: MinimizedChat) => {
    setEditingChatId(chat.id);
    setEditingTitle(chat.title || 'New Chat');
  }, []);

  const handleSaveRename = useCallback(async (chatId: string) => {
    if (!editingTitle.trim()) {
      toast('Chat title cannot be empty', 'error');
      setEditingChatId(null);
      return;
    }

    // Find the current chat to compare titles
    const chat = chats.find(c => c.id === chatId);
    const currentTitle = chat?.title || '';

    // Don't save if title hasn't changed (trimmed comparison)
    if (editingTitle.trim() === currentTitle.trim()) {
      setEditingChatId(null);
      setEditingTitle('');
      return;
    }

    try {
      await renameChat(chatId, editingTitle.trim());
      setEditingChatId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  }, [editingTitle, chats, renameChat, toast]);

  const handleCancelRename = useCallback(() => {
    setEditingChatId(null);
    setEditingTitle('');
  }, []);

  // Cancel any pending rename when the user navigates to a different chat.
  // While a row is in rename mode, its title span is replaced by an
  // `<input onClick={e => e.preventDefault()}>` *inside* the NavLink, which
  // swallows clicks on that row and makes the chat appear unclickable. If
  // the user navigates elsewhere and that row stays in rename mode, the
  // next click on it does nothing — fix it by exiting rename mode whenever
  // the active chat changes.
  useEffect(() => {
    setEditingChatId(null);
    setEditingTitle('');
  }, [currentChatId]);

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, chatId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename(chatId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);

  const handleChatClick = useCallback((e: React.MouseEvent, chat: MinimizedChat) => {
    if (windowWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [windowWidth, setIsSidebarOpen]);


  const loadMoreChats = useCallback(() => {
    if (isLoadingMore || !hasMoreChats) return;
    
    setIsLoadingMore(true);
    
    setTimeout(() => {
      const nextPage = currentPage + 1;
      const endIndex = nextPage * CHATS_PER_PAGE;
      const newChats = sortedChats.slice(0, endIndex);
      
      setPaginatedChats(newChats);
      setCurrentPage(nextPage);
      setHasMoreChats(endIndex < sortedChats.length);
      setIsLoadingMore(false);
    }, 500);
  }, [currentPage, sortedChats, isLoadingMore, hasMoreChats, CHATS_PER_PAGE]);

  // Notify parent component about pagination state changes
  useEffect(() => {
    if (onPaginationStateChange) {
      onPaginationStateChange(hasMoreChats, isLoadingMore, loadMoreChats);
    }
  }, [hasMoreChats, isLoadingMore, loadMoreChats, onPaginationStateChange]);



  const pinnedChatsList = useMemo(() => {
    const pinnedList = sortedChats.filter(chat => chat.isPinned);
    return pinnedList;
  }, [sortedChats]);


  const renderChatList = useCallback((chats: MinimizedChat[], title: string) => {
    if (chats.length === 0) return null;
    const isPinned = title === "Pinned";
    
    return (
      <>
        <h4 className={`sidebar-section-title ${isPinned ? 'pinned' : ''}`}>
          {title}
          {isPinned && chats.length > 0 && (
            <span className="chat-counter">{chats.length}</span>
          )}
        </h4>
        <div className={`sidebar-section ${isPinned ? 'pinned' : ''}`}>
          <ul>
            {chats.map((chat) => (
              <li key={chat.id} className={`sidebar-item-container ${removingChatId === chat.id ? 'removing' : ''}`}>
                <NavLink
                  to={`/${chat.id}`}
                  onClick={(e) => handleChatClick(e, chat)}
                  className={({isActive}) => isActive ? 'active' : ''}
                >
                  {editingChatId === chat.id ? (
                    <input
                      type="text"
                      className="sidebar-item-title-input"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => handleSaveRename(chat.id)}
                      onKeyDown={(e) => handleRenameKeyDown(e, chat.id)}
                      onClick={(e) => e.preventDefault()}
                      autoFocus
                    />
                  ) : (
                    <span 
                      className="sidebar-item-title"
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStartRename(chat);
                      }}
                    >
                      {chat.title || 'New Chat'}
                    </span>
                  )}
                  <SidebarItemDropdown
                    chat={chat}
                    onPinToggle={handleTogglePin}
                    onDeleteChat={handleRemoveChat}
                    onRenameChat={handleStartRename}
                  />
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }, [handleChatClick, handleTogglePin, handleRemoveChat, editingChatId, editingTitle, removingChatId, handleSaveRename, handleRenameKeyDown, handleStartRename]);

  useEffect(() => {
    if (isSidebarOpen) {      
      if (currentChatId === 'new') return;

      const activeChatElement = document.querySelector(`a[href='/${currentChatId}']`);
      if (activeChatElement) {
        activeChatElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isSidebarOpen, currentChatId]);

  return (
    <div className="sidebar-section sidebar-section-wrapper">
      <div className="chat-lists">
        {isChatsLoading ? (
          <SidebarChatsSkeleton />
        ) : (
          <>
            {sortedChats.length > 0 ? (
              <>
                {pinnedChatsList.length > 0 && renderChatList(pinnedChatsList, "Pinned")}
                {renderChatList(categorizedChats.today, "Today")}
                {renderChatList(categorizedChats.yesterday, "Yesterday")}
                {renderChatList(categorizedChats.lastWeek, "Last 7 Days")}
                {renderChatList(categorizedChats.lastMonth, "Last 30 Days")}
                {renderChatList(categorizedChats.older, "Older")}
                
                {isLoadingMore && (
                  <div className="loading-more-container">
                    <div className="loading-more-spinner" />
                    <span className="loading-more-text">Loading more chats...</span>
                  </div>
                )}
              </>
            ) : (
              <div className="no-chats-container">
                <p className="no-chats-text">No chats yet</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatList;
